/**
 * AddToHomeScreenPrompt — Sprint 19
 *
 * iOS Safari does NOT expose a beforeinstallprompt API. Users must manually
 * use Share → "Add to Home Screen". We guide them with a gentle, step-by-step UI.
 *
 * Detection logic:
 *   - Is running in iOS Safari (navigator.standalone not defined + iOS)
 *   - NOT already in standalone mode
 *   - Not shown before (localStorage flag)
 *   - Not dismissed permanently
 *
 * Android/Chrome: uses native beforeinstallprompt event for proper prompt.
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const LS_KEY = 'prime_a2hs_dismissed'
const LS_INSTALLED = 'prime_a2hs_installed'

function isIosSafari() {
  const ua = window.navigator.userAgent
  const isIos = /iphone|ipad|ipod/i.test(ua)
  // Safari on iOS doesn't have Chrome or Firefox in UA
  const isSafari = /safari/i.test(ua) && !/crios|fxios|opios|chrome/i.test(ua)
  return isIos && isSafari
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

export default function AddToHomeScreenPrompt() {
  const [show, setShow]             = useState(false)
  const [platform, setPlatform]     = useState(null) // 'ios' | 'android'
  const [deferredPrompt, setDeferred] = useState(null) // Android native prompt

  useEffect(() => {
    // Already installed or dismissed permanently
    if (localStorage.getItem(LS_INSTALLED)) return
    if (localStorage.getItem(LS_KEY) === 'permanent') return

    // If already in standalone, mark as installed and hide forever
    if (isStandalone()) {
      localStorage.setItem(LS_INSTALLED, '1')
      return
    }

    // Android: capture beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault()
      setDeferred(e)
      setPlatform('android')
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall)

    // iOS Safari: show guided prompt — T4·F3·A2 · MOMENTO DE VALOR, no primera carga.
    // Pedir "instálame" a alguien que abre la app por primera vez convierte mal e irrita. Se pide en
    // la VUELTA (2ª visita+): el usuario ya demostró intención · el momento de valor. Umbral exacto → cata.
    if (isIosSafari()) {
      // R6 (retorno-sep · 2026-09-09) · NO TAPAR EL CONSENTIMIENTO. Visto en el simulador
      // iOS: en la 2ª visita, si el usuario aún no ha resuelto las cookies, este prompt y
      // CookieConsentBanner se montan a la vez (App.js:333 y :337, hermanos sin condición
      // entre ellos) y se solapan en el borde inferior — con el consentimiento, que es
      // OBLIGACIÓN LEGAL, quedando debajo de una promoción de instalación.
      // El consentimiento va primero: mientras no esté resuelto, este prompt no aparece.
      // No se pierde nada: vuelve a evaluarse en la siguiente carga.
      let consentResuelto = false
      try { consentResuelto = !!window.localStorage.getItem('pl_cookie_consent') } catch (_) { consentResuelto = false }
      if (!consentResuelto) {
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      }
      const VISITS_KEY = 'prime_a2hs_visits'
      const visits = (parseInt(localStorage.getItem(VISITS_KEY) || '0', 10) || 0) + 1
      localStorage.setItem(VISITS_KEY, String(visits))
      if (visits >= 2) {
        const timer = setTimeout(() => {
          setPlatform('ios')
          setShow(true)
        }, 8000) // 8s tras cargar (no intrusivo)
        return () => {
          clearTimeout(timer)
          window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
        }
      }
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
  }, [])

  const handleDismissLater = () => {
    setShow(false)
    // Don't set permanent — show again next session
  }

  const handleDismissPermanent = () => {
    localStorage.setItem(LS_KEY, 'permanent')
    setShow(false)
  }

  const handleInstalled = () => {
    localStorage.setItem(LS_INSTALLED, '1')
    setShow(false)
  }

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      handleInstalled()
    } else {
      setShow(false)
    }
    setDeferred(null)
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0, transition: { type: 'spring', damping: 20, stiffness: 300 } }}
          exit={{ opacity: 0, y: 80, transition: { duration: 0.2 } }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            padding: '0 16px 16px',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              background: 'var(--prime-cream, #FAF9F5)',
              borderRadius: 20,
              padding: '20px 20px 16px',
              boxShadow: '0 -2px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06)',
              pointerEvents: 'all',
              maxWidth: 480,
              margin: '0 auto',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              {/* Prime Lab icon placeholder */}
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'var(--prime-terracota, #C4603C)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <span style={{ fontSize: 22, color: 'white', fontWeight: 700, fontFamily: 'sans-serif' }}>P</span>
              </div>
              <div>
                <p style={{
                  margin: 0, fontSize: 16, fontWeight: 600,
                  color: 'var(--pl-ink)',
                  fontFamily: 'var(--font-sans)',
                }}>
                  Añade Prime Lab a tu pantalla de inicio
                </p>
                <p style={{
                  margin: '2px 0 0', fontSize: 13,
                  color: 'var(--prime-ink-soft, #888)',
                  fontFamily: 'var(--font-sans)',
                }}>
                  Accede como app, sin navegador
                </p>
              </div>
            </div>

            {/* iOS steps */}
            {platform === 'ios' && (
              <div style={{
                background: 'rgba(0,0,0,0.03)',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 16,
                fontSize: 13,
                color: 'var(--pl-ink)',
                fontFamily: 'var(--font-sans)',
                lineHeight: 1.7,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--prime-terracota, #C4603C)',
                    color: 'white', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginTop: 1,
                  }}>1</span>
                  <span>Toca el icono <strong>Compartir</strong> (⬆) de Safari</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--prime-terracota, #C4603C)',
                    color: 'white', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginTop: 1,
                  }}>2</span>
                  <span>Baja y toca <strong>"Añadir a pantalla de inicio"</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--prime-terracota, #C4603C)',
                    color: 'white', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginTop: 1,
                  }}>3</span>
                  <span>Toca <strong>"Añadir"</strong> — ya tienes Prime Lab en tu home</span>
                </div>
              </div>
            )}

            {/* Android native install button */}
            {platform === 'android' && (
              <button
                onClick={handleAndroidInstall}
                style={{
                  width: '100%',
                  height: 50,
                  borderRadius: 14,
                  border: 'none',
                  background: 'var(--prime-terracota, #C4603C)',
                  color: 'white',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                Instalar Prime Lab
              </button>
            )}

            {/* iOS actions */}
            {platform === 'ios' && (
              <button
                onClick={handleInstalled}
                style={{
                  width: '100%',
                  height: 50,
                  borderRadius: 14,
                  border: 'none',
                  background: 'var(--prime-terracota, #C4603C)',
                  color: 'white',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                Ya lo añadí ✓
              </button>
            )}

            {/* Secondary actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleDismissLater}
                style={{
                  flex: 1, height: 40, border: 'none', background: 'transparent',
                  color: 'var(--prime-ink-soft, #888)', fontFamily: 'var(--font-sans)',
                  fontSize: 14, cursor: 'pointer',
                }}
              >
                Lo haré más tarde
              </button>
              <button
                onClick={handleDismissPermanent}
                style={{
                  height: 40, border: 'none', background: 'transparent',
                  color: 'var(--prime-ink-soft, #aaa)', fontFamily: 'var(--font-sans)',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                No mostrar más
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
